import { useMemo, useState } from "react";
import {
  DATE_PRESETS,
  DATE_PRESET_LABEL,
  resolveDatePreset,
  type DatePreset,
} from "@dashboard/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type DateRangeValue =
  | { kind: "preset"; preset: DatePreset; from: string; to: string }
  /** `ui`: Voreinstellung des Zeitraum-Waehlers, die die API nicht kennt (z. B. "dby", "last90") - geht als from/to raus. */
  | { kind: "custom"; from: string; to: string; ui?: string };

export function defaultDateRange(preset: DatePreset = "last7"): DateRangeValue {
  const { from, to } = resolveDatePreset(preset);
  return { kind: "preset", preset, from, to };
}

/** URL params for the API layer — either preset or from/to. */
export function toQueryParams(v: DateRangeValue): {
  preset?: DatePreset;
  from?: string;
  to?: string;
} {
  if (v.kind === "preset") return { preset: v.preset };
  return { from: v.from, to: v.to };
}

export function formatRangeLabel(v: DateRangeValue): string {
  if (v.kind === "preset") return DATE_PRESET_LABEL[v.preset];
  return `${v.from} → ${v.to}`;
}

interface Props {
  value: DateRangeValue;
  onChange: (v: DateRangeValue) => void;
  className?: string;
}

const SELECT_CUSTOM = "__custom__";

export function DateRangePicker({ value, onChange, className }: Props) {
  const [customOpen, setCustomOpen] = useState(value.kind === "custom");
  const [from, setFrom] = useState<string>(value.from);
  const [to, setTo] = useState<string>(value.to);

  const selectValue = value.kind === "preset" ? value.preset : SELECT_CUSTOM;

  const handleSelect = (v: string) => {
    if (v === SELECT_CUSTOM) {
      setCustomOpen(true);
      return;
    }
    const preset = v as DatePreset;
    const { from: f, to: t } = resolveDatePreset(preset);
    setFrom(f);
    setTo(t);
    setCustomOpen(false);
    onChange({ kind: "preset", preset, from: f, to: t });
  };

  const applyCustom = () => {
    if (!from || !to || from > to) return;
    onChange({ kind: "custom", from, to });
  };

  const invalid = useMemo(() => !from || !to || from > to, [from, to]);

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      <Select value={selectValue} onValueChange={handleSelect}>
        <SelectTrigger className="w-[200px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DATE_PRESETS.map((p) => (
            <SelectItem key={p} value={p}>
              {DATE_PRESET_LABEL[p]}
            </SelectItem>
          ))}
          <SelectItem value={SELECT_CUSTOM}>Benutzerdefiniert…</SelectItem>
        </SelectContent>
      </Select>

      {customOpen && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => setFrom(e.target.value)}
            className="h-9 rounded-md border border-border bg-card px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <span className="text-muted-foreground">→</span>
          <input
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.target.value)}
            className="h-9 rounded-md border border-border bg-card px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <Button size="sm" onClick={applyCustom} disabled={invalid}>
            Anwenden
          </Button>
        </div>
      )}

      <span className="text-xs text-muted-foreground">
        {value.from} → {value.to}
      </span>
    </div>
  );
}
